import { RainGaugeLocation } from '../../../models/location-card.model';
import { AutocompleteField, FieldType, SelectField, TextField } from '../../models/field-models';
import { LocationCardState } from '../../reducers';
import { findFieldById, findSectionById, normalizeSeriesName } from '../base-utils';
import {  BasicInfoCommonFieldIds, ConfigureAlarmsFieldId, DataDeliveryFieldId, TAssembledApiData } from '../common.models';
import {
    ECHO_MONITOR_CONFIGURATION_CONSTANTS,
    EchoAlarmFieldIds,
    EchoInfoFieldIds,
    EchoSectionIds,
    LongRangeFieldIds,
} from './base-config-models';
import {
    ChannelInstallationFieldIds,
    FlumeInstallationFieldIds,
    PipeInstallationFieldIds,
    WeirInstallationFieldIds,
    AdvancedFieldId,
} from '../common.models';
import { INSTALLATION_DEFAULT_CONSTANTS } from './installation-types/config-models';
import { DefinedInstallationType, INSTALLATION_TYPE, InstallationType } from 'app/shared/models/location-details';
import { EchoConfigurationPayload, EchoLocationPayload } from './base-api-models';
import { EchoEmailRecipient, EchoLocationCardState, EchoSmsRecipient } from './models';
import { LocationStatus } from 'app/shared/models/locations';

export function gatherEchoBasicInfoPayload(state: EchoLocationCardState): EchoLocationPayload[] {
    let mp1Info: Partial<EchoLocationPayload> = {};
    Object.keys(SaveBasicInfoPayload).forEach((key) => {
        mp1Info[key] = SaveBasicInfoPayload[key](state);
    });

    const installationType = SaveInstallationPayload(state);
    mp1Info = { ...mp1Info, ...installationType };

    return [mp1Info as EchoLocationPayload];
}


export const SaveBasicInfoPayload: { [key: string]: (state: EchoLocationCardState) => unknown } = {
    locationName: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.LocationName)!.value,
    ipaddress: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.IpAddress)!.value,
    assignedRainGaugeId: (state) => {
        const rainGaugeField = findFieldById(state.formState, BasicInfoCommonFieldIds.AssignedRainGauge) as SelectField<
            RainGaugeLocation,
            string
        >;
        const rainGauge = rainGaugeField.value;

        if (!rainGauge) {
            return null;
        }

        const assignedRainGauge = state.options.rainGauges.data.find((rg) => rg.locationName === rainGauge);

        if (!assignedRainGauge) {
            return null;
        }

        return assignedRainGauge.locationId;
    },
    communicationType: (state) => Number(findFieldById(state.formState, BasicInfoCommonFieldIds.CommunicationType)!.value),
    description: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Description)!.value,
    installationDate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.InstallationDate)!.value,
    latitude: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Latitude)!.value,
    longitude: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Longitude)!.value,
    manholeaddress: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ManholeAddress)!.value,
    manholedepth: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ManholeDepth)!.value,
    qFinalEntityID: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.QFinalEntityID)!.value,
    serialnumber: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SerialNumber)!.value,
    series: (state) => normalizeSeriesName(findFieldById(state.formState, BasicInfoCommonFieldIds.Series)!.value as string),
    status: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Status)!.value,
    maintenanceInterval: (state) => {
        const status = findFieldById(state.formState, BasicInfoCommonFieldIds.Status).value;
        return status === LocationStatus.Maintenance
            ? findFieldById(state.formState, BasicInfoCommonFieldIds.MaintenanceInterval)?.value ?? null
            : null;
    },
    apnProvider: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ApnProvider)!.value,
    samplerate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SampleRate)!.value,
    fastrate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.FastRate)!.value,
    scanRate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ScanRate)!.value,
    usedaylightsaving: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.UseDaylightSaving)!.value,
    synctime: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SyncTime)!.value,
    customProperties: (state) => {
        const customPropertiesSection = findSectionById(state.formState, BasicInfoCommonFieldIds.CustomPropertiesSection);
        const customProperties: { key: string; value: string }[] = [];

        let i = 0;
        while (true) {
            if (customPropertiesSection!.fields[`${BasicInfoCommonFieldIds.CustomPropertyKey}-${i}`] === undefined) {
                break;
            }

            const keyField = customPropertiesSection!.fields[
                `${BasicInfoCommonFieldIds.CustomPropertyKey}-${i}`
            ] as AutocompleteField<string, string>;

            const valueField = customPropertiesSection!.fields[
                `${BasicInfoCommonFieldIds.CustomPropertyValue}-${i}`
            ] as TextField;

            customProperties.push({
                key: keyField.value,
                value: valueField.value,
            });

            i++;
        }

        return customProperties;
    },
};

export const SaveInstallationPayload = (state: EchoLocationCardState) => {
    const basicInfo = state.formState.config.sections.find((s) => s.id === EchoSectionIds.BasicInfo);

    const installationTypeField = basicInfo?.fields[BasicInfoCommonFieldIds.InstallationType] as SelectField<
        InstallationType,
        string
    >;
    const installationType = installationTypeField.value;

    if (!installationType) {
        console.error('Installation type not found');
        return null;
    }
    const [type, shape] = installationType.split(':').map((s) => s.trim());

    if (type === INSTALLATION_TYPE.PIPE) {
        return {
            pipe: {
                height: findFieldById(state.formState, PipeInstallationFieldIds.Height)!.value,
                width: findFieldById(state.formState, PipeInstallationFieldIds.Width)!.value,
                lookupDesc: type,
                hc: findFieldById(state.formState, PipeInstallationFieldIds.Hc)!.value,
                roughness: findFieldById(state.formState, PipeInstallationFieldIds.Roughness)!.value,
                effectiveRoughness: findFieldById(state.formState, PipeInstallationFieldIds.EffectiveRoughness)!.value,
                slope: findFieldById(state.formState, PipeInstallationFieldIds.Slope)!.value,
                frictionFactor: findFieldById(state.formState, PipeInstallationFieldIds.FrictionFactor)!.value,
                nominalDiameter: findFieldById(state.formState, PipeInstallationFieldIds.NominalDiameter)!.value,
                startDate: findFieldById(state.formState, PipeInstallationFieldIds.StartDate)!.value,
                installationType: type,
                installationID: DefinedInstallationType.Pipe,
                shapeType: shape,
                installationShapeType: shape,
                MonitoringPoint: 1,
                ...INSTALLATION_DEFAULT_CONSTANTS,
            },
            installationID: DefinedInstallationType.Pipe,
            installationtypeid: DefinedInstallationType.Pipe,
            installationShapeType: shape,
        };
    }

    if (type === INSTALLATION_TYPE.WEIR) {
        return {
            weir: {
                range: findFieldById(state.formState, WeirInstallationFieldIds.Range)!.value,
                capacity: findFieldById(state.formState, WeirInstallationFieldIds.Capacity)!.value,
                height: findFieldById(state.formState, WeirInstallationFieldIds.Height)!.value,
                coefficient: findFieldById(state.formState, WeirInstallationFieldIds.Coefficient)!.value,
                length: findFieldById(state.formState, WeirInstallationFieldIds.Length)!.value,
                breadth: findFieldById(state.formState, WeirInstallationFieldIds.Breadth)!.value,
                lookupDesc: type,
                installationType: type,
                MonitoringPoint: 1,
                installationID: DefinedInstallationType.Weir,
                shapeType: shape,
                installationShapeType: shape,
                ...INSTALLATION_DEFAULT_CONSTANTS,
            },
            installationID: DefinedInstallationType.Weir,
            installationtypeid: DefinedInstallationType.Weir,
            installationShapeType: shape,
        };
    }

    if (type === INSTALLATION_TYPE.FLUME) {
        return {
            flume: {
                range: findFieldById(state.formState, FlumeInstallationFieldIds.Range)!.value,
                capacity: findFieldById(state.formState, FlumeInstallationFieldIds.Capacity)!.value,
                width: findFieldById(state.formState, FlumeInstallationFieldIds.Width)!.value,
                lookupDesc: type,
                installationType: type,
                MonitoringPoint: 1,
                installationID: DefinedInstallationType.Flume,
                shapeType: shape,
                installationShapeType: shape,
                ...INSTALLATION_DEFAULT_CONSTANTS,
            },
            installationID: DefinedInstallationType.Flume,
            installationtypeid: DefinedInstallationType.Flume,
            installationShapeType: shape,
        };
    }

    if (type === INSTALLATION_TYPE.CHANNEL) {
        const entriesSection = findSectionById(state.formState, ChannelInstallationFieldIds.ArraySection);
        const numberOfEntriesField = findFieldById(state.formState, ChannelInstallationFieldIds.NumberOfEntries, FieldType.Number);
        const rangeField = findFieldById(state.formState, ChannelInstallationFieldIds.Range, FieldType.Number);

        if (!entriesSection || !numberOfEntriesField || !rangeField) {
            console.error('Channel installation fields not found');
            return null;
        }

        const numberOfEntries = numberOfEntriesField.value;
        const entries: { depth: number; quantity: number }[] = [];

        for (let i = 0; i < Number(numberOfEntries); i++) {
            const depthField = entriesSection.fields[`${ChannelInstallationFieldIds.Depth}-${i}`];
            const quantityField = entriesSection.fields[`${ChannelInstallationFieldIds.Quantity}-${i}`];

            if (depthField?.type !== FieldType.Number || quantityField?.type !== FieldType.Number) {
                console.error('Channel installation entry fields not found');
                return null;
            }

            entries.push({
                depth: depthField.value,
                quantity: quantityField.value,
            });
        }

        return {
            channel: {
                range: rangeField.value,
                numberOfEntries: numberOfEntries,
                entries: entries,
                lookupDesc: type,
                installationType: type,
                MonitoringPoint: 1,
                installationID: DefinedInstallationType.Channel,
                shapeType: shape,
                installationShapeType: shape,
                ...INSTALLATION_DEFAULT_CONSTANTS,
            },
            installationID: DefinedInstallationType.Channel,
            installationtypeid: DefinedInstallationType.Channel,
            installationShapeType: shape,
        };
    }

    console.error('Installation type not supported');
    return null;
};

export function gatherEchoConfigurationInfo(state: EchoLocationCardState): EchoConfigurationPayload {

    const emailRec = [1,2,3,4,5].flatMap(i =>
        buildEmailRecipient(state, AdvancedFieldId[`CheckInEmailRecipient${i}`])
    );
    const smsRec = [1,2,3,4,5].flatMap(i =>
        buildSmsRecipient(state, {
            recipient: AdvancedFieldId[`CheckInTextRecipient${i}`],
            startHour: AdvancedFieldId[`CheckInTextRec${i}StartHour`],
            endHour: AdvancedFieldId[`CheckInTextRec${i}EndHour`],
        })
    );

    const result: { [key: string]: (state: EchoLocationCardState) => unknown } = {
        prismdeliveryipaddress: (state) => findFieldById(state.formState, DataDeliveryFieldId.PrismDeliveryIPAddress).value,
        prismdeliveryfast: (state) => findFieldById(state.formState, DataDeliveryFieldId.PrismDeliveryFast).value,
        prismdeliverynormal: (state) => findFieldById(state.formState, DataDeliveryFieldId.PrismDeliveryNormal).value,
        silt: (state) => findFieldById(state.formState, EchoInfoFieldIds.Silt).value,
        samplerate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SampleRate).value,
        usedaylightsaving: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.UseDaylightSaving).value,
        synctime: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SyncTime).value,
        apnProvider: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ApnProvider).value,
        apn: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ApnProvider).value,
        fastrate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.FastRate).value,
        scanRate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ScanRate).value,
        lowLevelEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.LowLevelEnable).value,
        lowLevelThreshold: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.LowLevelThreshold).value,
        fullPipeEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.FullPipeEnable).value,
        fullPipethreshold: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.FullPipeThreshold).value,
        highLevelEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighLevelEnable).value,
        highLevelThreshold: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighLevelThreshold).value,
        highHighEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighHighEnable).value,
        highHighThreshold: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighHighThreshold).value,
        overflowEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.OverflowEnable).value,
        ipaddress: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.IpAddress).value,
        manholedepth: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ManholeDepth).value,
        physicalOffset: (state) => findFieldById(state.formState, LongRangeFieldIds.PhysicalOffset).value,
        batteryLowEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.BatteryLowEnable).value,
        batteryLowThreshold: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.BatteryLowThreshold).value,
        tiltEnable: (state) => findFieldById(state.formState, EchoAlarmFieldIds.tiltEnable).value,
        tiltThreshold: (state) => findFieldById(state.formState, EchoAlarmFieldIds.tiltThreshold).value,
        unidepthtoaverage: (state) => findFieldById(state.formState, EchoAlarmFieldIds.UnidepthToAverage).value,

        blanking: (state) => findFieldById(state.formState, LongRangeFieldIds.Blanking).value,
        power: (state) => findFieldById(state.formState, LongRangeFieldIds.Power).value,
        compensatedTemperatureMarch: (state) =>
            findFieldById(state.formState, LongRangeFieldIds.CompensatedTemperatureMarch).value,
        compensatedTemperatureSeptember: (state) =>
            findFieldById(state.formState, LongRangeFieldIds.CompensatedTemperatureSeptember).value,
        pressureStore: (state) => findFieldById(state.formState, LongRangeFieldIds.PressureStore).value,
        storeData: (state) => findFieldById(state.formState, LongRangeFieldIds.StoreData).value,
        temperatureSensor: (state) => findFieldById(state.formState, LongRangeFieldIds.TemperatureSensor).value,
        gainControl: (state) => findFieldById(state.formState, LongRangeFieldIds.GainControl).value,
        electronicOffset: (state) => findFieldById(state.formState, LongRangeFieldIds.ElectronicOffset).value,
        unidepthFiltering: (state) => findFieldById(state.formState, LongRangeFieldIds.UnidepthFiltering).value,

        storetemperature: (state) => findFieldById(state.formState, EchoInfoFieldIds.StoreTemperature).value,
        storebattery: (state) => findFieldById(state.formState, EchoInfoFieldIds.BatteryVoltage).value,
        hcValue: (state) => findFieldById(state.formState, EchoInfoFieldIds.HcValue).value,
        hc: (state) => findFieldById(state.formState, EchoInfoFieldIds.HcValue).value,
        pipeHeight: (state) => findFieldById(state.formState, PipeInstallationFieldIds.Height)?.value ?? null,
        serialNumber: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SerialNumber).value,
        connectionType: (state) => Number(findFieldById(state.formState, BasicInfoCommonFieldIds.CommunicationType).value),
        checkinenable: (state) => findFieldById(state.formState, AdvancedFieldId.CheckInEnable).value,
        checkinhour: (state) => findFieldById(state.formState, AdvancedFieldId.CheckInHour).value,
        smsrecipients: () => smsRec.filter((v) => !!v),
        emailrecipients: () => emailRec.filter((v) => !!v)
    };

    return Object.keys(result).reduce(
        (acc, key) => {
            acc[key] = result[key](state);
            return acc;
        },
        { ...ECHO_MONITOR_CONFIGURATION_CONSTANTS },
    ) as EchoConfigurationPayload;
}

function buildSmsRecipient(state: EchoLocationCardState,ids: {
        recipient: AdvancedFieldId;
        startHour: AdvancedFieldId;
        endHour: AdvancedFieldId;
    }
): EchoSmsRecipient[] {

    return [{
        recipient: findFieldById(state.formState, ids.recipient).value as string,
        startHour: Number(findFieldById(state.formState, ids.startHour).value),
        endHour: Number(findFieldById(state.formState, ids.endHour).value),
    }];
}


function buildEmailRecipient(state: EchoLocationCardState,id: AdvancedFieldId): EchoEmailRecipient[] {
        return [{
            recipient: findFieldById(state.formState, id).value as string
        }];
}

